import { RefObject, useEffect, useRef, useState } from 'react';

export const useResizing = <T extends HTMLElement>(
  ref: RefObject<T>,
  debounce = 300,
  threshold = 10 // threshold in pixels for width changes
): boolean => {
  const [is_resizing, setResizing] = useState<boolean>(false);
  const last_width = useRef<number | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    let timeout: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width } = entry.contentRect;

      if (last_width.current !== null) {
        const delta = Math.abs(last_width.current - width);
        if (delta < threshold) {
          return;
        }
      }

      last_width.current = width;

      setResizing(true);
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        setResizing(false);
      }, debounce);
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [ref, debounce, threshold]);

  return is_resizing;
};
